<template>
  <div class="flex flex-col gap-6">
    <!-- Basuppgifter -->
    <SectionCard title="Kunduppgifter">
      <div class="flex max-w-full flex-col gap-5 xl:max-w-[50%]">
        <DataSection>
          <DataField label="Personnummer">{{ user.personalNumber }}</DataField>
          <DataField label="E-post">{{ user.contactDetails.email }}</DataField>
          <DataField label="Telefon">{{ user.contactDetails.phone }}</DataField>
          <DataField label="Adress"
            >{{ formatAddress(user.contactDetails) }} <br />{{ user.contactDetails.zip }}
            {{ user.contactDetails.city }}</DataField
          >
        </DataSection>
      </div>
    </SectionCard>

    <!-- Ansökningar -->
    <SectionCard title="Ansökningar">
      <p v-if="!user.applications.length" class="px-3 py-2 text-sm text-slate-500">Inga ansökningar.</p>
      <AppTable v-else>
        <thead>
          <tr>
            <th>Typ</th>
            <th>Status</th>
            <th>Adress</th>
            <th>Inskickad</th>
          </tr>
        </thead>
        <tbody>
          <AppTableRow
            v-for="app in user.applications"
            :key="app.id"
            :route-location="applicationRouteLocation({ id: app.id, type: app.type, status: app.status })"
          >
            <AppTableCell>{{ translateRef('applicationTypes', app.type) }}</AppTableCell>
            <AppTableCell>{{ translateRef('applicationStatuses', app.status) }}</AppTableCell>
            <AppTableCell>{{ formatAddress(app.collateralProperty) }}</AppTableCell>
            <AppTableCell>{{ app.submittedAt ? formatDateIfValid(app.submittedAt) : '-' }}</AppTableCell>
          </AppTableRow>
        </tbody>
      </AppTable>
    </SectionCard>

    <!-- Bolån -->
    <SectionCard title="Bolån">
      <p v-if="!user.debts.length" class="px-3 py-2 text-sm text-slate-500">Inga bolån.</p>
      <AppTable v-else>
        <thead>
          <tr>
            <th>Adress</th>
            <th>Status</th>
            <th>Aktiverad</th>
          </tr>
        </thead>
        <tbody>
          <AppTableRow v-for="debt in user.debts" :key="debt.id" :route-location="`/bolan/${debt.id}`">
            <AppTableCell>
              {{ formatAddress(debt.collateralProperty) }},
              {{
                debt.collateralProperty.housingAssociation
                  ? debt.collateralProperty.housingAssociation + ' ' + debt.collateralProperty.apartmentNumberBrf
                  : debt.collateralProperty.municipality?.name + ' ' + debt.collateralProperty.propertyLabel
              }}
            </AppTableCell>
            <AppTableCell>{{ translateRef('debtLifeCycleStatuses', debt.status) }}</AppTableCell>
            <AppTableCell>{{ formatDateIfValid(debt.startDate) }}</AppTableCell>
          </AppTableRow>
        </tbody>
      </AppTable>
    </SectionCard>

    <!-- Handpenningslån -->
    <SectionCard title="Handpenningslån">
      <p v-if="!user.depositLoans.length" class="px-3 py-2 text-sm text-slate-500">Inga handpenningslån.</p>
      <AppTable v-else>
        <thead>
          <tr>
            <th>Adress</th>
            <th>Status</th>
            <th>Aktiverad</th>
          </tr>
        </thead>
        <tbody>
          <AppTableRow
            v-for="depositLoan in user.depositLoans"
            :key="depositLoan.id"
            :route-location="depositLoan.status === 'active' ? `/handpenningslan/${depositLoan.id}` : undefined"
          >
            <AppTableCell>
              {{ formatAddress(depositLoan.collateralProperty) }},
              {{
                depositLoan.collateralProperty.housingAssociation
                  ? depositLoan.collateralProperty.housingAssociation +
                    ' ' +
                    depositLoan.collateralProperty.apartmentNumberBrf
                  : [depositLoan.collateralProperty.municipality?.name, depositLoan.collateralProperty.propertyLabel]
                      .filter(Boolean)
                      .join(' ') || '-'
              }}
            </AppTableCell>
            <AppTableCell>{{ translateRef('debtLifeCycleStatuses', depositLoan.status) }}</AppTableCell>
            <AppTableCell>{{ depositLoan.startDate ? formatDateIfValid(depositLoan.startDate) : '-' }}</AppTableCell>
          </AppTableRow>
        </tbody>
      </AppTable>
    </SectionCard>

    <!-- Autogiro -->
    <SectionCard title="Autogiro">
      <AutogiroSection :user-id="user.userId" />
    </SectionCard>

    <!-- Betalningsavier -->
    <SectionCard title="Betalningsavier">
      <InvoiceTable :user-id="user.userId" />
    </SectionCard>

    <!-- KYC -->
    <SectionCard title="KYC">
      <KycSection :kycs="user.kycs" :user-id="user.userId" :on-kyc-submitted="onKycSubmitted" />
    </SectionCard>
  </div>
</template>

<script setup lang="ts">
import { SectionCard, AppTable, AppTableRow, AppTableCell } from '@staff/ui'
import { formatDateIfValid } from '@staff/shared'

import DataField from '@/components/DataField.vue'
import DataSection from '@/components/DataSection.vue'
import AutogiroSection from './components/AutogiroSection.vue'
import InvoiceTable from './components/InvoiceTable.vue'
import KycSection from './components/KycSection.vue'
import { useMetadata } from '@/composables/useMetadata'
import { formatAddress } from '@/utils/formatAddress'
import { applicationRouteLocation } from '@/router/applicationRouteLocations'
import type { UserDetails } from '@/services/user'

defineProps<{ user: UserDetails; onKycSubmitted: () => boolean | Promise<boolean> }>()

const { translateRef } = useMetadata()
</script>
